/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import moment from 'moment';
import { logger } from '@touchplan/logging';

export class DateUtils {
	// convert UTC time to local time
	public static convertUTCDateToLocalDate(date: Date): Date {
		const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
		const offset = date.getTimezoneOffset() / 60;
		const hours = date.getHours();
		localDate.setHours(hours - offset);
		return localDate;
	}

	public static dateToStandardStringFormat(d: Date) {
		return moment(d).format('YYYY-MM-DD');
	}

	/**
	 * Convert a "YYYY-MM-DD" format string to a Date in the local timezone:
	 *
	 *     {MM} {DD} {YYYY} 00:00:00 {local timezone}
	 */
	public static stringToLocalTimezoneDate(s: string) {
		if (s && typeof s !== 'string') {
			logger.error("stringToLocalTimezoneDate: argument is not a string; it's a " + typeof s + '; value:', s);
			return null;
		}
		if (s && s.trim() !== '') {
			const date = new Date(s);
			// TP-3275:
			// So the string's YYYY-MM-DD format is parsed into a Date that's viewed as
			//
			//     {MM} {DD} {YYYY} 00:00:00 GMT
			//
			// But then if given to a Material calendar, it's displayed relative to the
			// local timezone.  If - say - that's Eastern Standard Time, then that same
			// date/time becomes:
			//
			//     {MM} {DD-1} {YYYY} 19:00:00 GMT-0500 (Eastern Standard Time)
			//
			// Note that that's a day EARLIER.
			// That messes things up.
			//
			// SO, to avoid that problem, the following code adjust the Date to be:
			//
			//     {MM} {DD} {YYYY} 00:00:00 {local timezone}
			//
			const utcYear = date.getUTCFullYear();
			const utcMonth = date.getUTCMonth(); // 0 based, i.e. January === 0
			const utcDate = date.getUTCDate();

			// TP-4788
			// We need to be very careful here, because setting the year, month and date
			// separately can lead to problems.  For instance, if the Date is in a state
			// where it represents Oct-31 and you set the month to Nov, the "setMonth"
			// call will SILENTLY say "November doesn't have a 31-st date, so I'm gonna
			// make the month be December (aren't I helpful?)", and that's EXTREMELY
			// confusing, i.e. the cause behind TP-4788.  This single call avoids problems
			// resulting from setting the date components one at a time...
			date.setFullYear(utcYear, utcMonth, utcDate);

			date.setHours(0, 0, 0, 0);
			// logger.log('date', date);
			return date;
		}

		// String was null-ish or empty:
		return null;
	}

	public static getShortDayString(date: Date) {
		return moment(date).format('ddd');
	}

	public static getMonthString(date: Date) {
		return moment(date).format('MMMM');
	}

	public static getWeekDayMonthDay(date: Date) {
		const mDate = moment(date);
		return { weekDay: mDate.format('dd'), dayOfMonth: mDate.format('D') };
	}
}
